import { mergeAttributes, Node } from '@tiptap/core';
import { is_trusted_iframe_src, trusted_iframe_origins } from '../../../lib/trustedIframeOrigins';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    iframe: {
      setIframe: (options: {
        src: string;
        title?: string;
        width?: string;
        height?: string;
      }) => ReturnType;
    };
  }
}

const sanitize_dimension = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return /^[0-9]+(%|px)?$/.test(trimmed) ? trimmed : null;
};

const get_valid_iframe_attrs = (attrs: Record<string, unknown>) => {
  const src = typeof attrs.src === 'string' ? attrs.src.trim() : '';
  if (!src || !is_trusted_iframe_src(src)) return null;

  return {
    src,
    title:
      typeof attrs.title === 'string' && attrs.title.trim()
        ? attrs.title.trim()
        : 'Embedded content',
    width: sanitize_dimension(attrs.width) || '100%',
    height: sanitize_dimension(attrs.height),
  };
};

export const IframeExtension = Node.create({
  name: 'iframe',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      title: { default: 'Embedded content' },
      width: { default: '100%' },
      height: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'iframe',
        getAttrs: element => {
          const src = element.getAttribute('src')?.trim() ?? '';
          if (!src || element.hasAttribute('srcdoc') || !is_trusted_iframe_src(src)) {
            return false;
          }
          return {
            src,
            title: element.getAttribute('title') ?? 'Embedded content',
            width: element.getAttribute('width') ?? '100%',
            height: element.getAttribute('height') || null,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'iframe',
      mergeAttributes(HTMLAttributes, {
        loading: 'lazy',
        sandbox: 'allow-scripts allow-same-origin allow-popups allow-forms allow-presentation',
        referrerpolicy: 'strict-origin-when-cross-origin',
        allow:
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
        allowfullscreen: '',
      }),
    ];
  },

  addCommands() {
    return {
      setIframe:
        options =>
        ({ commands }) => {
          if (trusted_iframe_origins.size === 0) return false;
          const attrs = get_valid_iframe_attrs(options);
          if (!attrs) return false;
          return commands.insertContent({ type: this.name, attrs });
        },
    };
  },
});
